// Spielen gegen Bots, Ansicht: Brett mit Spielerleisten, Gegnerwahl (botpick.ts), Lernhilfen
// (playhelp.ts, im Training playtrain.ts), Zugliste.
import { h, type VNode } from 'snabbdom';
import { board } from '../board';
import { t } from '../i18n';
import { materialLead } from '../material';
import { saveGame } from '../playapi';
import type { PlayCtrl, Side } from '../playctrl';
import { botName } from '../playbots';
import { isPublic, setFocus } from '../util';
import { playerBar } from './playerbar';
import { promotionView } from './promotion';
import { botPick } from './botpick';
import { coachPanel } from './playhelp';
import { trainBoard, trainPanel } from './playtrain';
import { guessView } from './guess';
import { askAgain, canAskAgain } from '../playturn';
import { guessHint, startInfo } from './playfrom';

function status(c: PlayCtrl): VNode {
  if (!c.started) return h('p.muted', t('Stärke und Farbe wählen, dann „Neue Partie“.'));
  if (c.result) return h('p.play-result', c.result);
  if (c.thinking) return h('p.muted', t('{n} denkt …', { n: botName(c.bot) }));
  return h('p', c.pos.turn === c.me ? t('Du bist am Zug.') : '');
}

function moveList(c: PlayCtrl): VNode {
  const rows: VNode[] = [];
  for (let i = 0; i < c.moves.length; i += 2) {
    rows.push(h('span.index', String(i / 2 + 1)));
    rows.push(h('span.move', c.moves[i].san));
    rows.push(h('span.move', c.moves[i + 1]?.san ?? ''));
  }
  return h('div.tview.play-moves', { hook: { postpatch: (_, v) => ((v.elm as HTMLElement).scrollTop = 1e6) } }, rows);
}

/** Leiste über/unter dem Brett: oben der Gegner, unten du; unten rechts letzter Zug oder Ergebnis */
function bar(c: PlayCtrl, where: 'top' | 'bottom'): VNode {
  const mine = (where === 'bottom') !== c.flipped;
  const color = mine ? c.me : c.me === 'white' ? 'black' : 'white';
  const last = c.moves[c.moves.length - 1];
  const move = last ? `${Math.ceil(c.moves.length / 2)}${c.moves.length % 2 ? '.' : '…'} ${last.san}` : '';
  return playerBar(where, {
    color,
    name: mine ? t('Du') : botName(c.bot),
    active: c.started && !c.result && c.pos.turn === color,
    lead: materialLead(c.fen) * (color === 'white' ? 1 : -1),
    busy: !mine && c.thinking,
    info: where === 'bottom' ? c.result ?? move : '',
    strong: where === 'bottom' && !!c.result,
  });
}

export function playView(c: PlayCtrl): VNode {
  if (c.guess) return guessView(c, c.guess);
  const last = c.moves[c.moves.length - 1];
  const turn = c.pos.turn;
  const sideBtn = (s: Side, label: string) =>
    h('label.chk', [
      h('input', { attrs: { type: 'radio', name: 'play-side' }, props: { checked: c.side === s }, on: { change: () => c.setSide(s) } }),
      ' ' + label,
    ]);
  const newGame = h('div.fld', [h('button.btn.accent', { on: { click: () => c.newGame() } }, t('Neue Partie'))]);
  return h('section.play', { class: { running: c.started && !c.result } }, [
    h('div.play-body', [
      h('div.board-col', [
        bar(c, 'top'),
        board('play-board', {
          fen: c.fen,
          orientation: (c.flipped ? (c.me === 'white' ? 'black' : 'white') : c.me),
          turnColor: turn,
          check: c.pos.isCheck() ? turn : false,
          lastMove: last?.last,
          coordinates: true,
          animation: { enabled: true, duration: 250 },
          movable: { free: false, color: c.started && !c.result ? c.me : undefined, dests: c.dests(), showDests: true, events: { after: c.userMove } },
          premovable: { enabled: false },
          draggable: { enabled: true, showGhost: true },
          highlight: { lastMove: true, check: true },
          drawable: { enabled: true, visible: true, autoShapes: c.coach.shapes() },
          ...(c.train && !c.train.done ? trainBoard(c.train) : {}),
        }),
        c.promotion ? promotionView(c.me, r => c.promote(r)) : null,
        bar(c, 'bottom'),
      ]),
      h('div.side', [
        // während der Partie ist die Gegnerwahl gesperrt und steht im Kopf der Partie: Platz für die Lernhilfen
        h('div.setup', c.started && !c.result ? [newGame] : [
          h('div.hd', h('h3', t('Gegner'))),
          botPick(c),
          h('div.fld', [
            h('span.lbl', t('Ich spiele')),
            h('div.row', [sideBtn('white', t('Weiß')), sideBtn('black', t('Schwarz')), sideBtn('random', t('Zufall'))]),
          ]),
          startInfo(c),
          newGame,
          c.started || c.start ? null : guessHint(),
        ]),
        c.train ? trainPanel(c, c.train) : coachPanel(c),
        h('div.setup', [
          h('div.hd', [h('h3', c.started ? `${c.me === 'white' ? t('Du (Weiß)') : t('Du (Schwarz)')} – ${botName(c.bot)}` : t('Partie'))]),
          h('div.fld', [status(c)]),
          c.moves.length ? moveList(c) : null,
          c.started
            ? h('div.fld', [
                h('div.row', [
                  !c.result ? h('button.btn', { on: { click: () => c.resign() } }, t('Aufgeben')) : null,
                  h('button.btn', { on: { click: () => ((c.flipped = !c.flipped), c.redraw()) } }, t('Drehen')),
                  h('button.btn', { attrs: { title: t('Vollbild (z)') }, on: { click: () => setFocus(true, c.redraw) } }, t('Vollbild')),
                  c.result && c.moves.length && !c.saved && !isPublic ? h('button.btn', { on: { click: () => saveGame(c) } }, t('In der Bibliothek speichern')) : null,
                  c.saved ? h('a.link', { attrs: { href: '#/bibliothek' } }, t('Gespeichert. Zur Bibliothek')) : null,
                ]),
              ])
            : null,
          c.error ? h('div.fld', [h('p.error', c.error), canAskAgain(c) ? h('div.row', h('button.btn', { on: { click: () => askAgain(c) } }, t('Gegner erneut fragen'))) : null]) : null,
        ]),
      ]),
    ]),
  ]);
}
